import React, { Component } from "react";
import styled from "styled-components";

import { Icon } from "js/Components/Icon";
import { RoundIconButton } from "js/Components/IconButton";
import { StyledScrollBox } from "js/Components/StyledScrollBox";
import { isInternalBAIUser } from "js/config";
import AppController from "js/core/AppController";
import DebugController from "js/core/DebugController";
import { Key } from "js/core/utilities/keys";
import { stopPropagation } from "js/core/utilities/stopPropagation";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { PropertyPanelContainer, PropertySection } from "js/EditorComponents/PropertyPanel";
import { WithLabel } from "js/Components/WithLabel";
import { Button } from "js/Components/Button";
import AnimationPanel from "js/react/views/AnimationPanel/AnimationPanel";
import { _ } from "js/vendor";

import { EditorCommentsPane } from "js/react/views/CommentsPane/EditorCommentsPane";
import RecordPanel from "../../react/views/RecordPanel/RecordPanel";
import AddCalloutPanel from "../PresentationEditor/Components/AddCalloutPanel";
import AdvanceNextSlidePanel from "../PresentationEditor/Components/AdvanceNextSlidePanel";
import { RevisionPanel } from "../PresentationEditor/Components/RevisionPanel";
import { StylePanel } from "../PresentationEditor/Components/StylePanel";
import SearchBarPanel from "../SeachBar/SearchBarPanel";
import DebugPanel from "./DebugPanel";
import { IdeasPanel } from "./IdeasPanel";
import LayoutPanel from "./LayoutPanel";
import ManageSlidePanel from "./ManageSlidePanel";
import { RecordPropertyPanel } from "./RecordPropertyPanel";
import SlideAiIdeasPanel from "../AiGeneration/SlideAi/SlideAiIdeasPanel";

const Container = styled.div`
    color: black;
    background: #f1f1f1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    height: 100%;
    overflow: hidden;
`;

export const LockShield = styled.div`
    pointer-events: ${props => (props.isLocked ? "none" : "auto")};
    opacity: ${props => (props.isLocked ? 0.5 : 1)};
    width: 100%;
    overflow: auto;
    height: inherit;
`;

const CollapsePanelButton = styled.div`
    position: absolute;
    right: -30px;
    top: 6px;

    .bai-icon {
        font-size: 28px;
        color: white;
    }

    transition: right 300ms;
`;

const DebugPanelButton = styled.div`
    position: absolute;
    right: -12px;
    top: 48px;

    .bai-icon {
        font-size: 36px;
    }

    transition: right 300ms;
`;

class InnerEditorPropertyPanel extends Component {
    constructor(props) {
        super(props);

        this.isLockedFromPanel = false;
    }

    shouldComponentUpdate(nextProps) {
        const { isCanvasGenerating: willCanvasBeGenerating, isCanvasInErrorState: willCanvasBeInErrorState, isCanvasRendered: willCanvasBeRendered } = nextProps;
        const { isCanvasRendered } = this.props;

        if (willCanvasBeGenerating) {
            // Prevent refresh during canvas generation
            return false;
        }

        if ((!isCanvasRendered || (isCanvasRendered && !willCanvasBeRendered)) && !willCanvasBeInErrorState) {
            // Prevent refresh when canvas isn't rendered
            return false;
        }

        return true;
    }

    componentDidMount() {
        const { isCanvasInErrorState } = this.props;

        if (isCanvasInErrorState) {
            PresentationEditorController.setSelectedPropertyPanelTab("versions");
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const { selectedPropertyPanelTab, selectedElements, isCurrentSlideLockedByMe, isCanvasInErrorState } = this.props;

        if (prevProps.isCurrentSlideLockedByMe !== isCurrentSlideLockedByMe) {
            if (!isCurrentSlideLockedByMe || (!this.isLockedFromPanel && isCurrentSlideLockedByMe)) {
                this.isLockedFromPanel = false;
            }
        }

        if (prevProps.isCanvasInErrorState !== isCanvasInErrorState) {
            clearTimeout(this.setVersionsTabTimeout);

            if (isCanvasInErrorState && selectedPropertyPanelTab !== "versions") {
                this.setVersionsTabTimeout = setTimeout(() => {
                    if (this.props.isCanvasInErrorState) {
                        // Still in error state, show versions tab
                        PresentationEditorController.setSelectedPropertyPanelTab("versions");
                    }
                }, 2000);
                return;
            }
        }

        if (
            selectedPropertyPanelTab !== "element" &&
            !isCanvasInErrorState &&
            !_.isEqual(prevProps.selectedElements.map(el => el.uniquePath), selectedElements.map(el => el.uniquePath)) &&
            selectedElements.length > 0 &&
            !selectedElements[0]?.isInstanceOf("VideoOverlay")
        ) {
            PresentationEditorController.setSelectedPropertyPanelTab("element");
        }
    }

    handleKeyDown = event => {
        if (event.which === Key.KEY_Z && (event.metaKey || event.ctrlKey) || PresentationEditorController.selectedPropertyPanelTab === "comments") {
            return;
        }

        stopPropagation()(event);
    }

    handleLockShieldMouseDown = event => {
        const { isCurrentSlideLockedByMe, currentCanvasController, allowSharedSlideEditing, currentSlide } = this.props;

        const isLocked = currentSlide.isLibrarySlide() && !allowSharedSlideEditing;

        if (isLocked) {
            event.stopPropagation();
            currentCanvasController.editLibrarySlide();
            return;
        }

        if (!isCurrentSlideLockedByMe) {
            this.isLockedFromPanel = true;
            currentCanvasController.lockSlideForCollaborators(10);
        }
    }

    handleLockShieldMouseLeave = () => {
        const { isCurrentSlideLockedByMe, currentCanvasController } = this.props;

        if (isCurrentSlideLockedByMe && this.isLockedFromPanel) {
            currentCanvasController.unlockSlideForCollaborators();
        }
    }

    handleTabChange = async tab => {
        const { currentCanvasController } = this.props;

        await currentCanvasController.selectionLayerController.setSelectedElements([]);
        await PresentationEditorController.setSelectedPropertyPanelTab(tab);
    }

    render() {
        const {
            currentCanvasController,
            currentSelectionLayerController,
            currentSlide,
            isCanvasRendered,
            isCanvasInErrorState,
            selectedPropertyPanelTab,
            showPropertyPanel,
            allowSharedSlideEditing,
            isCurrentSlideLockedForMe,
            isSingleSlideEditor,
            selectedElements,
        } = this.props;

        const isLocked = (selectedPropertyPanelTab !== "manage-slide" && currentSlide.isLibrarySlide() && !allowSharedSlideEditing) || isCurrentSlideLockedForMe;

        if (selectedPropertyPanelTab === "versions") {
            return (
                <Container onMouseDown={stopPropagation()} onKeyDown={this.handleKeyDown}>
                    <LockShield
                        isLocked={isLocked}
                        onMouseDown={this.handleLockShieldMouseDown}
                        onMouseLeave={this.handleLockShieldMouseLeave}
                    >
                        <RevisionPanel
                            currentCanvasController={currentCanvasController}
                            currentSlide={currentCanvasController.slide}
                            currentSelectionLayerController={currentSelectionLayerController}
                            onClose={() => PresentationEditorController.setSelectedPropertyPanelTab("element")}
                            canClose={!isCanvasInErrorState}
                        />
                    </LockShield>
                </Container>
            );
        }

        if (selectedPropertyPanelTab === "find_and_replace") {
            return (
                <Container onMouseDown={stopPropagation()} onKeyDown={this.handleKeyDown}>
                    <SearchBarPanel
                        onMouseDown={this.handleLockShieldMouseDown}
                        onMouseLeave={this.handleLockShieldMouseLeave}
                        hideShowBar={() => {
                            PresentationEditorController.setSelectedPropertyPanelTab(this.props.selectedPropertyPanelTabProps.selectedPropertyPanelTab);
                            PresentationEditorController.handleShowSearchForText(false);
                        }} />
                </Container>
            );
        }

        if (!isCanvasRendered) {
            return null;
        }

        if (!currentCanvasController.canvasBundle) {
            return null;
        }

        const getPanel = () => {
            const Panel = (() => {
                switch (selectedPropertyPanelTab) {
                    case "add":
                        return <LayoutPanel {...this.props} />;
                    case "styles":
                        return <StylePanel {...this.props} />;
                    case "record":
                        return <RecordPropertyPanel {...this.props} />;
                    case "advance":
                        return <AdvanceNextSlidePanel {...this.props} />;
                    case "ideas":
                        return <IdeasPanel {...this.props} />;
                    case "animate":
                        return (
                            <PropertyPanelContainer>
                                {!isSingleSlideEditor && <AdvanceNextSlidePanel {...this.props} />}
                                <RecordPanel {...this.props} />
                                <AnimationPanel {...this.props} />
                            </PropertyPanelContainer>
                        );
                    case "callouts":
                        return <AddCalloutPanel {...this.props} onClose={() => PresentationEditorController.setSelectedPropertyPanelTab("layout")} />;
                    case "manage-slide":
                        return <ManageSlidePanel {...this.props} />;
                    case "debug":
                        return <DebugPanel {...this.props} />;
                    case "element":
                        const { SelectedElementPropertyPanel } = currentCanvasController.canvasBundle;
                        return <SelectedElementPropertyPanel {...this.props} />;
                    case "comments":
                        return <EditorCommentsPane {...this.props} />;
                    case "slide-ai":
                        return <SlideAiIdeasPanel {...this.props} />;
                }
            })();

            return (
                <LockShield
                    isLocked={isLocked}
                    onMouseDown={this.handleLockShieldMouseDown}
                    onMouseLeave={this.handleLockShieldMouseLeave}
                >
                    <StyledScrollBox>{Panel}</StyledScrollBox>
                </LockShield>
            );
        };

        return (
            <Container onMouseDown={stopPropagation()} onKeyDown={this.handleKeyDown}>
                {getPanel()}
                {showPropertyPanel && (
                    <CollapsePanelButton>
                        <Icon fill onClick={() => PresentationEditorController.hidePropertyPanel()}>keyboard_arrow_left</Icon>
                    </CollapsePanelButton>
                )}
                {isInternalBAIUser(AppController.user.getEmail()) && DebugController.debugButtonVisible && (
                    <DebugPanelButton style={{ right: showPropertyPanel ? -35 : -35 }}>
                        <RoundIconButton size={24} fill icon="bug_report" onClick={() => this.handleTabChange("debug")} />
                    </DebugPanelButton>
                )}
            </Container>
        );
    }
}

export const EditorPropertyPanel = PresentationEditorController.withFullState(
    InnerEditorPropertyPanel,
    // Skip refresh when freezePropertiesPanel is true
    (props, nextProps) => {
        // Prevent the search bar panel to close when we select an element that
        // is not in the current slide
        if (nextProps.showFindAndReplaceBar) {
            return false;
        }

        if (props.selectedPropertyPanelTab == "nav" && nextProps.selectedPropertyPanelTab == "nav") {
            return false;
        }

        return nextProps.freezePropertiesPanel !== true && nextProps.freezeSelectionLayer !== true;
    }
);
